// FormTemplateList.js - Formulärmallar (patientformulär): globala standardformulär + klinikens egna
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../../apiClient';
import { SCORING_METHOD_LABELS } from './scoringOptions';
import './FormTemplates.css';

function FormTemplateList() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await apiClient.get('/api/form-templates');
      setTemplates(resp.data.templates || []);
    } catch (err) {
      console.error('Fel vid hämtning av formulärmallar:', err);
      setError(err.response?.data?.msg || 'Kunde inte hämta formulärmallar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const globals = templates.filter(t => t.is_global);
  const own = templates.filter(t => !t.is_global);

  const handleCopy = async (template) => {
    const title = window.prompt('Namn på det nya egna formuläret:', `${template.title} (kopia)`);
    if (title === null) return;
    try {
      setBusyId(template.template_id);
      const resp = await apiClient.post(`/api/form-templates/${template.template_id}/copy`, { title });
      toast.success(`"${resp.data.title}" skapat som eget formulär`);
      navigate(`/formularmallar/edit/${resp.data.template_id}`);
    } catch (err) {
      console.error('Kopiering misslyckades:', err);
      toast.error(err.response?.data?.msg || 'Kunde inte kopiera formuläret');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      setBusyId(template.template_id);
      await apiClient.put(`/api/form-templates/${template.template_id}`, { is_active: !template.is_active });
      toast.success(template.is_active ? 'Formuläret är inaktiverat' : 'Formuläret är aktiverat');
      await load();
    } catch (err) {
      console.error('Kunde inte ändra status:', err);
      toast.error(err.response?.data?.msg || 'Kunde inte ändra status');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Ta bort formuläret "${template.title}"?`)) return;
    try {
      setBusyId(template.template_id);
      await apiClient.delete(`/api/form-templates/${template.template_id}`);
      toast.success('Formuläret är borttaget');
      setTemplates(prev => prev.filter(t => t.template_id !== template.template_id));
    } catch (err) {
      console.error('Borttagning misslyckades:', err);
      toast.error(err.response?.data?.msg || 'Kunde inte ta bort formuläret');
    } finally {
      setBusyId(null);
    }
  };

  const scoringLabel = (t) => {
    const method = t.scoring?.method || 'none';
    if (method === 'none') return 'Ingen poäng';
    const base = SCORING_METHOD_LABELS[method] || method;
    return t.scoring?.label ? `${base} · ${t.scoring.label}` : base;
  };

  const renderCard = (t, { readOnly }) => (
    <div key={t.template_id} className={`ft-card ${t.is_active ? '' : 'ft-card-inactive'}`}>
      <div className="ft-card-info">
        <div className="ft-card-title-row">
          <h3 className="ft-card-title">{t.title}</h3>
          {readOnly && <span className="ft-badge ft-badge-global"><i className="fas fa-lock"></i> Standard</span>}
          {!t.is_active && <span className="ft-badge ft-badge-inactive">Inaktivt</span>}
        </div>
        {t.description && <p className="ft-card-desc">{t.description}</p>}
        <p className="ft-card-meta">
          <span><i className="fas fa-list-ol"></i> {t.question_count} frågor</span>
          <span><i className="fas fa-calculator"></i> {scoringLabel(t)}</span>
          <span><i className="fas fa-paper-plane"></i> Skickat {t.sent_count} gånger</span>
        </p>
      </div>
      <div className="ft-card-actions">
        {readOnly ? (
          <>
            <button className="ft-btn" onClick={() => navigate(`/formularmallar/edit/${t.template_id}`)}>
              <i className="fas fa-eye"></i> Visa
            </button>
            <button className="ft-btn ft-btn-primary" disabled={busyId === t.template_id}
              onClick={() => handleCopy(t)} title="Skapa en redigerbar kopia för din klinik">
              <i className="fas fa-copy"></i> Kopiera som eget
            </button>
          </>
        ) : (
          <>
            <button className="ft-btn ft-btn-primary" onClick={() => navigate(`/formularmallar/edit/${t.template_id}`)}>
              <i className="fas fa-edit"></i> Redigera
            </button>
            <button className="ft-btn" disabled={busyId === t.template_id} onClick={() => handleCopy(t)}>
              <i className="fas fa-copy"></i> Kopiera
            </button>
            <button className="ft-btn" disabled={busyId === t.template_id} onClick={() => handleToggleActive(t)}>
              <i className={`fas ${t.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>{' '}
              {t.is_active ? 'Inaktivera' : 'Aktivera'}
            </button>
            <button className="ft-btn ft-btn-danger" disabled={busyId === t.template_id} onClick={() => handleDelete(t)}>
              <i className="fas fa-trash"></i> Ta bort
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="ft-list-container">
      <div className="ft-list-header">
        <div>
          <h2><i className="fas fa-clipboard-list"></i> Formulärmallar</h2>
          <p className="ft-list-intro">
            Formulär som skickas till patienten via Bokningsappen. Standardformulären är
            validerade instrument och kan inte ändras — kopiera dem för att anpassa.
            Poängen räknas ut automatiskt när patienten svarar och visas bara för personalen.
          </p>
        </div>
        <button className="ft-btn ft-btn-create" onClick={() => navigate('/formularmallar/create')}>
          <i className="fas fa-plus"></i> Skapa eget formulär
        </button>
      </div>

      {error && <p className="ft-error"><i className="fas fa-exclamation-circle"></i> {error}</p>}
      {loading && <p className="ft-muted">Hämtar formulärmallar…</p>}

      {!loading && (
        <>
          <section className="ft-section">
            <h3 className="ft-section-title"><i className="fas fa-globe"></i> Standardformulär</h3>
            {globals.length === 0
              ? <p className="ft-muted">Inga standardformulär finns.</p>
              : globals.map(t => renderCard(t, { readOnly: !t.can_edit }))}
          </section>

          <section className="ft-section">
            <h3 className="ft-section-title"><i className="fas fa-hospital"></i> Egna formulär</h3>
            {own.length === 0
              ? <p className="ft-muted">Kliniken har inga egna formulär ännu. Skapa ett nytt eller kopiera ett standardformulär.</p>
              : own.map(t => renderCard(t, { readOnly: false }))}
          </section>
        </>
      )}
    </div>
  );
}

export default FormTemplateList;
